import type { vec4 } from 'gl-matrix'
import type { FC } from 'react'
import { useEffect, useRef } from 'react'

import type {
  HorizonAlignment,
  Nullable,
  Point2D,
  ScaleType,
  VerticalAlignment,
} from '../../../types'
import { requestRedraw } from '../../../utils'
import { useWebGlContext } from '../context'
import { useFontAtlas } from '../FontAtlas'
import { addRender } from '../helpers'
import { LabelRender } from './LabelRender'

type LabelProps = {
  scale: ScaleType
  point: Point2D
  rotation?: number
  fontSize: number
  text: string
  thickness: number
  color: vec4
  outline?: boolean
  outlineThickness?: number
  outlineColor?: vec4
  horizonAlignment?: HorizonAlignment
  verticalAlignment?: VerticalAlignment
  fillBackground?: boolean
  backgroundColor?: vec4
  isScaling?: boolean
}

export const Label: FC<LabelProps> = ({
  scale,
  point,
  fontSize,
  text,
  thickness,
  color,
  outline = false,
  outlineThickness,
  outlineColor,
  rotation = 0,
  horizonAlignment = 'left',
  verticalAlignment = 'top',
  fillBackground = false,
  backgroundColor,
  isScaling,
}) => {
  const {
    canvas: { value: containerElement },
    renderQueue: { value: renders },
    update: { value: update, setValue: setUpdate },
    programs,
    textures,
  } = useWebGlContext()
  const labelRenderRef = useRef<Nullable<LabelRender>>(null)
  const {
    font: { value: font },
  } = useFontAtlas()

  const dispose = () => {
    labelRenderRef.current?.[Symbol.dispose]()
    labelRenderRef.current = null
    requestRedraw(setUpdate)
  }

  useEffect(() => {
    if (labelRenderRef.current || !containerElement || !font) return dispose

    labelRenderRef.current = new LabelRender(
      containerElement,
      programs,
      textures,
      scale,
      point,
      rotation,
      font,
      fontSize,
      text,
      1,
      thickness,
      color,
      outline,
      outlineThickness,
      outlineColor,
      horizonAlignment,
      verticalAlignment,
      fillBackground,
      backgroundColor,
      isScaling,
    )

    requestRedraw(setUpdate)

    return dispose
  }, [containerElement, font, setUpdate])

  useEffect(() => {
    if (!labelRenderRef.current) return

    labelRenderRef.current.setZoomState(scale)

    requestRedraw(setUpdate)
  }, [scale, setUpdate])

  useEffect(() => {
    if (!labelRenderRef.current) return

    labelRenderRef.current.setFontSize(fontSize)
    requestRedraw(setUpdate)
  }, [fontSize, setUpdate])

  useEffect(() => {
    if (!labelRenderRef.current || !backgroundColor) return

    labelRenderRef.current.setBackgroundColor(backgroundColor)
    requestRedraw(setUpdate)
  }, [backgroundColor, setUpdate])

  useEffect(() => {
    if (!labelRenderRef.current) return

    addRender(renders, labelRenderRef.current)
  }, [renders, update])

  useEffect(() => {
    if (!labelRenderRef.current) return

    const prevPoint = labelRenderRef.current.getPoint()
    if (prevPoint.x === point.x && prevPoint.y === point.y) return

    labelRenderRef.current.setPoint(point)
    requestRedraw(setUpdate)
  }, [point, setUpdate])

  return null
}
